import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

import { INAPP_BROWSER_TOOLBAR_COLOR } from '@/constants';
import matomoHelper from '@/helper/matomo';
import { router } from '@/router';
import type { ContentWithLink } from '@/types/feed-entrys';
import { getInternalLink } from './link-generation';
import { hasValidLink } from './link-validation';

/**
 * Handles clicking on content with a link, tracking the event and navigating appropriately.
 * Opens external links in a new window or in-app browser, navigates to internal links via router.
 * @param item - Content item with link information.
 * @param eventCategory - Category for Matomo event tracking (default: 'ContentWithLink').
 * @returns Promise that resolves when navigation is complete.
 */
export async function clickContentWithLink(
  item: ContentWithLink,
  eventCategory:
    | 'Stories'
    | 'Homepage Tile'
    | 'CampaignTeaser_homepage'
    | 'CampaignTeaser_contentpage'
    | 'ContentWithLink' = 'ContentWithLink',
): Promise<void> {
  if (!hasValidLink(item)) {
    return;
  }

  const title = item.headline || item.title || 'unknown';
  await matomoHelper.trackEvent({
    e_c: eventCategory,
    e_a: 'Click',
    e_n: title,
    e_v: 1,
  });

  const linkType = item.cta_type || item.linkType || item.type;
  const link = item.cta_link || item.link || null;
  if (linkType === 'link' && link) {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({
        url: link,
        toolbarColor: INAPP_BROWSER_TOOLBAR_COLOR,
      });
      return;
    }
    window.open(link, '_blank');
    return;
  }

  router.push(getInternalLink(item));
  return;
}
